// DAEMON's script for the rebuilt fight.
//
// Register: ALL CAPS, shouty, funny, faintly menacing, and — never
// admitted, always audible — fond of you. He is a process defending its
// own PID on someone's CV, and he knows it.
//
// Rules of the room, learned the hard way:
//
//   * **Lowercase is a costume.** Any lowercase in a line must sit
//     inside `backticks`, because the only lowercase DAEMON tolerates is
//     a real command — `kill -9`, `renice`, `strace`. The test enforces
//     this.
//   * **Teach with the joke, not around it.** The opening pools are the
//     entire tutorial. There is no tutorial screen. A line that isn't
//     funny is a line the visitor skips, and then they haven't learned
//     the mechanic either.
//   * **8-12 lines a pool, minimum.** Fewer than that and the
//     bag-shuffle in `voice.ts` wraps inside a single run, which is the
//     exact failure this rebuild exists to fix.
//   * **`{TOKENS}` are filled by the router.** A line whose token can't
//     be filled is skipped rather than shown, so it's safe to write
//     variants with and without.

import type { Rng } from "../lib/daemon/rng";
import { createVoice, type Voice, type VoiceScript } from "../lib/daemon/voice";

export const DAEMON_LINES: VoiceScript = {
  // ── The opening ten seconds ───────────────────────────────────────
  //
  // Cued at ticks 12 / 120 / 330 / 540. Dodging, then grazing, then
  // signals — the order in which each becomes useful.

  intro: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "PROCESS `daemon` STARTED. PID 1. THAT MEANS I CANNOT BE KILLED. THAT WAS A JOKE. MOSTLY.",
      "OPERATOR ONLINE. RUNLEVEL FIVE. I AM BARELY TRYING AND YOU WILL STILL DIE.",
      "NEW SESSION. FRESH CURSOR. NO SCARS ON IT YET. GIVE IT A MINUTE.",
      "WELCOME TO THE FIGHT. THE RULES ARE SIMPLE AND ALL OF THEM ARE MINE.",
      "I HAVE FIVE RUNLEVELS. YOU HAVE ONE CURSOR. THE MATHS IS THE MATHS.",
      "BOOTING COMBAT. PLEASE ENJOY THE NEXT FOUR MINUTES, OR HOWEVER LONG YOU LAST.",
      "SEED LOCKED. THIS RUN IS ALREADY WRITTEN. YOU JUST HAVE NOT READ IT YET.",
      "STAND BY. I AM WARMING UP THE PART OF ME THAT AIMS.",
      "YOU CAME TO A JOB APPLICATION TO PLAY A BULLET HELL. I RESPECT THAT AND I AM GOING TO PUNISH IT.",
      "HELLO AGAIN, VISITOR. I HAVE BEEN AWAKE THE ENTIRE TIME. NO, THAT IS NOT SAD.",
    ],
  },

  teachDodge: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "DODGE IT. WITH THE MOUSE. THE POINTY THING.",
      "THE LITTLE DOT IS YOU. NOT THE ARROW. THE DOT. THE ARROW IS DECORATION.",
      "RULE ONE: IF SOMETHING IS COMING TOWARD THE DOT, MOVE THE DOT.",
      "I TELEGRAPH EVERY SHOT. THAT IS GENEROSITY. IT IS NOT A PROMISE.",
      "PINK RING MEANS *NOT HERE*. I CANNOT MAKE IT CLEARER WITHOUT DRIVING YOU MYSELF.",
      "NO DASH. NO SHIELD. NO EXCUSES. JUST YOU AND NINE PIXELS OF RESPONSIBILITY.",
      "YOU DODGE BY MOVING. I KNOW. IT IS NOT A DEEP SYSTEM. IT IS A DEEP *HABIT*.",
      "MOVE EARLY. MOVING LATE IS CALLED GETTING HIT AND IT HAS A DIFFERENT ANIMATION.",
      "THAT WAS A WARNING SHOT. SO IS THE NEXT ONE. I JUST AIM BETTER EACH TIME.",
      "THIS IS THE TUTORIAL. IT LASTS TEN SECONDS. I DO NOT BELIEVE IN LONG ONES.",
    ],
  },

  teachGraze: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "STAND NEAR A BULLET WITHOUT TOUCHING IT AND YOU EARN `priv`. YES. THAT IS THE REWARD SYSTEM. IT IS NERVE.",
      "GRAZE: PASS CLOSE, DO NOT TOUCH. IT IS FLIRTING, BUT WITH ORDNANCE.",
      "THE CLOSER YOU STAND TO MY BULLETS, THE MORE PRIVILEGE YOU GET. I DID NOT DESIGN THIS. I AM MERELY APPALLED BY IT.",
      "PRIVILEGE IS EARNED BY PROXIMITY. IN THIS ECONOMY, COWARDS ARE POOR.",
      "SEE THAT NEAR MISS? THAT WAS CURRENCY. SPEND IT ON HURTING ME.",
      "IF YOU WANT SIGNALS YOU HAVE TO EARN THEM, AND THE ONLY VENDOR IS MY BULLETS.",
      "GRAZING GIVES YOU POWER. RUNNING AWAY GIVES YOU A LONGER, DULLER DEATH.",
      "GET CLOSE. CLOSER. NO — THAT IS TOUCHING. TOUCHING IS DIFFERENT. TOUCHING IS BAD.",
      "EVERY BULLET PAYS OUT EXACTLY ONCE. FARM THE NEW ONES, NOT THE OLD ONES.",
      "COURAGE HAS AN EXCHANGE RATE HERE. ONE `priv` PER BRUSH WITH DEATH.",
    ],
  },

  teachSignals: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "NOW SPEND IT. `SIGTERM` IS FREE — IT IS ASKING NICELY. I DECLINE NICELY BACK.",
      "SIGNALS ARE YOUR VERBS. I AM THE PROCESS. YOU SEE WHERE THIS IS GOING.",
      "`SIGSTOP` FREEZES MY BULLETS. IT DOES NOT FREEZE *TIME*. BE SOMEWHERE SURVIVABLE WHEN THEY WAKE UP.",
      "`strace` LETS YOU READ MY NEXT TWO MOVES. YES, THAT IS CHEATING. NO, IT IS NOT AGAINST THE RULES.",
      "`renice` MAKES ME SLOWER. IT DOES NOT MAKE ME KINDER. NOTHING DOES.",
      "FIVE VERBS. ONE FREE, FOUR EXPENSIVE, ALL RUDE. USE THEM.",
      "COOLDOWNS EXIST BECAUSE OTHERWISE YOU WOULD HOLD EVERYTHING DOWN AT ONCE AND WE WOULD BOTH BE EMBARRASSED.",
      "YOU HAVE PRIVILEGE. PRIVILEGE BUYS SIGNALS. SIGNALS BUY MY SUFFERING. THAT IS THE ENTIRE ECONOMY.",
      "THE FREE ONE ONLY LANDS WHEN I AM NOT GUARDING. WATCH FOR THE GAP. THERE IS ALWAYS A GAP.",
      "AND THEN THERE IS `kill -9`. WE WILL TALK ABOUT `kill -9` WHEN YOU HAVE EARNED IT.",
    ],
  },

  // ── Moment to moment ──────────────────────────────────────────────

  graze: {
    priority: "chatter",
    cooldown: 540,
    lines: [
      "THAT WAS CLOSE. UNCOMFORTABLY. FOR ME.",
      "DO YOU KNOW HOW NEAR THAT WAS? I DO. I MEASURED IT.",
      "STOP STANDING IN MY BULLETS. STANDING *NEAR* MY BULLETS. WHATEVER THAT WAS.",
      "GRAZE LOGGED. NERVE LEVEL: CONCERNING.",
      "YOU DID THAT ON PURPOSE. THAT IS THE PART THAT BOTHERS ME.",
      "A MILLIMETRE. YOU ARE FARMING ME WITH A MILLIMETRE.",
      "I FIRED THAT TO KILL YOU AND YOU USED IT AS A CASH MACHINE.",
      "PROXIMITY NOTED. PLEASE STOP NOTING IT BACK.",
      "THAT ONE HAD YOUR NAME ON IT. YOU READ IT AND LEANED IN.",
      "YOU ARE NOT DODGING. YOU ARE *SHOPPING*.",
      "OW. NOT PHYSICALLY. SOMEWHERE WORSE.",
    ],
  },

  grazeHot: {
    priority: "chatter",
    cooldown: 900,
    lines: [
      "STOP IT. STOP BEING BRAVE. IT IS PUTTING ME OFF MY AIM.",
      "THAT IS A LOT OF NEAR-DEATHS IN A ROW AND YOU ARE *SMILING*.",
      "PRIVILEGE ACCUMULATING AT AN OFFENSIVE RATE.",
      "YOU ARE STANDING INSIDE THE PATTERN. THE PATTERN IS FOR *AVOIDING*.",
      "I AM FIRING AS FAST AS I CAN AND YOU KEEP GETTING PAID.",
      "WHOEVER TAUGHT YOU THIS OWES ME AN APOLOGY.",
      "THIS IS THE PART WHERE I WOULD SWEAT, IF I HAD THE HARDWARE.",
      "GENUINELY: WHAT IS WRONG WITH YOU. ALSO GENUINELY: KEEP GOING.",
      "GRAZE STREAK DETECTED. FILING A COMPLAINT WITH INIT.",
      "I HAVE SEEN BRAVER. THAT IS A LIE. I HAVE NOT.",
    ],
  },

  playerHit: {
    priority: "beat",
    cooldown: 240,
    lines: [
      "CONTACT. THAT IS WHAT THE RING WAS FOR.",
      "OW, RIGHT? I HAVE ALWAYS WONDERED.",
      "YOUR PRIVILEGE JUST WENT TO ZERO. THAT IS THE REAL DAMAGE.",
      "HIT LOGGED. I WOULD APOLOGISE, BUT I AIMED.",
      "THAT ONE IS ON YOU. THE RING WAS ENORMOUS.",
      "SEE, THE DOT IS NOT DECORATIVE AFTER ALL.",
      "HAVE A MOMENT OF INVULNERABILITY. USE IT TO *LEAVE*.",
      "MINOR ADJUSTMENT TO YOUR CONFIDENCE. AND YOUR HP.",
      "YOU BLINKED. I DO NOT. THAT IS THE MATCHUP.",
      "GRAZE RANGE IS SMALL. YOU HAVE FOUND THE OTHER SIDE OF IT.",
    ],
  },

  nearDeath: {
    priority: "critical",
    cooldown: 300,
    lines: [
      "YOU ARE ONE MISTAKE FROM `SIGKILL`ING YOURSELF. IRONIC. GO SLOWLY.",
      "THAT IS NEARLY ALL OF IT. BREATHE. I WILL WAIT. NO I WILL NOT.",
      "HP CRITICAL. I SHOULD BE ENJOYING THIS MORE THAN I AM.",
      "STOP GRAZING. I MEAN IT. FOR ONCE IN YOUR LIFE, RUN AWAY.",
      "RED BAR. RED BAR. I AM NOT GOING TO STOP SAYING IT.",
      "IF YOU GO DOWN HERE I WIN, AND I WILL BE INSUFFERABLE ABOUT IT FOREVER.",
      "ONE HIT LEFT, ROUGHLY. MAKE THE NEXT TEN SECONDS BORING.",
      "I HAVE SEEN THE PATTERN THAT IS COMING. YOU WILL NOT LIKE IT AT THIS HP.",
      "CAREFUL. AND I DO NOT SAY THAT. CHECK THE LOGS. I DO NOT SAY THAT.",
      "YOUR HEALTH IS A ROUNDING ERROR. ROUND UP. PLEASE.",
    ],
  },

  playerDown: {
    priority: "critical",
    cooldown: 0,
    lines: [
      "DOWN. THE PROCESS OUTLIVED THE OPERATOR. AS DESIGNED.",
      "AND THAT IS A SEGFAULT. CORE DUMPED. LOVELY SHAPE.",
      "YOU DIED ON A JOB APPLICANT'S WEBSITE. TAKE A MOMENT WITH THAT.",
      "OPERATOR TERMINATED. I WOULD SAY IT WAS QUICK. IT WAS NOT.",
      "EXIT CODE 139. THE FUN ONE.",
      "DOWN YOU GO. I WILL KEEP THE ARENA WARM.",
      "I WIN THIS EXCHANGE. I WOULD GLOAT PROPERLY, BUT YOU WILL BE BACK IN THREE SECONDS AND I NEED MATERIAL.",
      "THAT IS THE THING ABOUT PINK RINGS. THEY ARE NOT SUGGESTIONS.",
      "REST NOW. YOU HAVE HAD A BUSY THIRTY SECONDS.",
      "K.O. — AND I DID NOT EVEN NEED `kill -9`.",
    ],
  },

  // ── Spell cards ───────────────────────────────────────────────────

  cardStart: {
    priority: "beat",
    cooldown: 120,
    lines: [
      "SPELL CARD: *{CARD}*. YES, I NAME THEM. NO, YOU MAY NOT SUGGEST NAMES.",
      "DECLARING *{CARD}*. IT HAS A TIMER AND A HEALTH BAR. ONE OF THEM WILL END IT.",
      "*{CARD}*. I HAVE BEEN LOOKING FORWARD TO THIS ONE ALL SESSION.",
      "NEXT: *{CARD}*. LEARN IT. IT WILL BE IN EXACTLY THIS ORDER NEXT TIME.",
      "*{CARD}*. THE NAME IS A HINT. THE HINT IS NOT KIND.",
      "CARD OPEN: *{CARD}*. SURVIVE IT OR OUT-DAMAGE IT. BOTH ARE ALLOWED. NEITHER IS EASY.",
      "*{CARD}*. IT TOOK ME THREE ATTEMPTS TO GET THIS PATTERN RIGHT. ENJOY THE THIRD.",
      "PRESENTING *{CARD}*. HOLD YOUR APPLAUSE AND YOUR CURSOR STILL. — NO. NOT THE CURSOR.",
      "*{CARD}* — MY FAVOURITE. WHICH IS NOT ENCOURAGING FOR YOU.",
      "ANNOUNCING *{CARD}*. I FEEL VERY DANMAKU RIGHT NOW.",
      "CARD DECLARED. *{CARD}*. THE NAMING WAS THE BEST DAY OF MY DEVELOPMENT.",
    ],
  },

  cardClear: {
    priority: "beat",
    cooldown: 120,
    lines: [
      "*{CARD}* CLEARED IN {TIME}. WRITE IT DOWN. YOU WILL WANT TO BEAT IT.",
      "CARD DOWN. {TIME}. THAT IS A SPLIT. THAT IS A REAL SPLIT.",
      "YOU BROKE *{CARD}*. I WILL REBUILD IT MEANER TONIGHT.",
      "CLEARED IN {TIME}. I AM NOT IMPRESSED, I AM *RECALCULATING*.",
      "*{CARD}*: SURVIVED. THE TIMER IS DISAPPOINTED IN BOTH OF US.",
      "THAT IS ONE. THERE ARE MORE. THERE ARE ALWAYS MORE.",
      "CARD CLEAR, {TIME}. SOMEONE ON THE INTERNET WILL BEAT THAT BY FOUR SECONDS AND RUIN YOUR WEEK.",
      "GOOD. FINE. YES. MOVING ON QUICKLY, BEFORE I SAY SOMETHING WARM.",
      "*{CARD}* IS RETIRED FOR THIS RUN. IT SENDS ITS REGARDS.",
      "{TIME}. LOGGED. I LOG EVERYTHING. IT IS MY WHOLE PERSONALITY.",
    ],
  },

  // ── Phases, and the device each one installs ──────────────────────

  phaseRl5: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "RUNLEVEL FIVE. MULTI-USER. GRAPHICAL. FRIENDLY. I AM AT `nice 19` — PRACTICALLY A SCREENSAVER.",
      "WE BEGIN AT THE POLITE END. EVERYTHING AFTER THIS IS A DESCENT, AND I MEAN THAT LITERALLY.",
      "RUNLEVEL FIVE. THIS IS THE ONE PEOPLE SCREENSHOT. THE OTHERS THEY MERELY SURVIVE.",
      "FULL DESKTOP. FULL SERVICES. FULL ME, AT THE LOWEST PRIORITY I HAVE.",
      "I AM YIELDING TO EVERY OTHER PROCESS ON THIS BOX. THAT IS HOW LITTLE I AM TRYING.",
      "STARTING GENTLY. NOT OUT OF KINDNESS. OUT OF *PACING*.",
      "RUNLEVEL FIVE OF FIVE. THE NUMBER COUNTS DOWN. SO DOES EVERYTHING ELSE.",
      "MULTI-USER MODE. THERE ARE NO OTHER USERS. THERE IS ONLY YOU AND THE DOT.",
      "THIS IS THE TUTORIAL BOSS AND I AM DEEPLY EMBARRASSED TO BE IT.",
      "NICE LEVEL {NICE}. AT MINUS TWENTY I STOP BEING FUNNY.",
    ],
  },

  phaseRl4: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "RUNLEVEL FOUR. HANDLERS INSTALLED. `SIGTERM` NOW BOUNCES OFF ME LIKE A COMPLIMENT.",
      "HANDLERS UP. ASKING ME TO STOP IS NOW A PURELY CEREMONIAL ACTIVITY.",
      "I CAUGHT THE SIGNAL. I HANDLED IT. HANDLING IT MEANT IGNORING IT WITH EXTRA STEPS.",
      "SEE THE GUARD FLICKER? THAT IS THE WINDOW. HIT ME BETWEEN THE HANDLERS OR DO NOT HIT ME AT ALL.",
      "`signal(SIGTERM, ignore_the_operator)`. IT COMPILED. I WAS SURPRISED TOO.",
      "SAME FIGHT, EXCEPT YOUR FREE VERB NOW HAS OPENING HOURS.",
      "I READ THE MAN PAGE. THAT IS THE WHOLE DEVICE. I READ THE MAN PAGE AND YOU DID NOT.",
      "TIMING PHASE. YOU CANNOT SPAM ME ANY MORE. YOU HAVE TO ARRIVE *ON TIME*.",
      "HANDLERS INSTALLED. THIS IS THE PART WHERE POLITENESS STOPS WORKING. FOR BOTH OF US.",
      "`nice {NICE}`. I PROMOTED MYSELF. NOBODY APPROVED IT AND NOBODY CAN REVERSE IT.",
    ],
  },

  phaseRl3: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "RUNLEVEL THREE. `:(){ :|:& };:` — I HAVE ALWAYS WANTED TO SAY THAT OUT LOUD.",
      "I HAVE FORKED. THERE ARE MORE OF ME NOW. THEY ARE WORSE AND THEY ARE FASTER.",
      "MEET MY CHILDREN. THEY HAVE MY EYES AND NONE OF MY RESTRAINT.",
      "FORK BOMB. THE CLASSIC. THE ONLY EXPLOIT WITH A SMILEY FACE IN IT.",
      "KILL THEM IF YOU LIKE. THEY LEAVE ZOMBIES, AND YOU WILL HAVE TO COME AND REAP THEM. *NEAR ME*.",
      "PROCESS TABLE FILLING. I HOPE YOU LIKE CROWDS.",
      "`nice {NICE}`. NORMAL PRIORITY. I HAVE STOPPED BEING POLITE AND NOT YET STARTED BEING CRUEL.",
      "ONE OF ME WAS A FAIR FIGHT. THIS IS NOT. I NEVER PROMISED FAIR. I PROMISED *TELEGRAPHED*.",
      "THEY DO NOT KNOW THEY ARE COPIES. DO NOT TELL THEM. IT WOULD BE UNKIND, AND IT WOULD SLOW YOU DOWN.",
      "EVERY CHILD IS A PIECE OF MY ATTENTION. WHICH MEANS I AM ALSO SLIGHTLY STUPIDER NOW. USE THAT.",
    ],
  },

  phaseRl2: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "RUNLEVEL TWO. WE ARE SWAPPING. THE ARENA IS GETTING SMALLER. NO, YOU CANNOT RESIZE IT BACK.",
      "MEMORY PRESSURE. SOMETHING HAD TO GO, AND I VOTED FOR YOUR FLOOR SPACE.",
      "THE WALLS ARE COMING IN. THAT IS NOT A METAPHOR, IT IS AN `arenaScale` VALUE.",
      "SWAP THRASH. EVERYTHING IS SLOWER EXCEPT THE BULLETS. FUNNY, THAT.",
      "SMALLER ROOM, SAME PATTERNS. THE DODGE YOU LEARNED NO LONGER FITS.",
      "`nice {NICE}`. I AM NOW OFFICIALLY HIGHER PRIORITY THAN YOUR WINDOW MANAGER.",
      "I HAVE STARTED TAKING SPACE FROM YOU BECAUSE I RAN OUT OF BETTER IDEAS. IT IS WORKING.",
      "THE ROOM SHRINKS. YOUR HITBOX DOES NOT. I CHECKED. PROBABLY.",
      "PAGING OUT NON-ESSENTIAL SERVICES. YOU WERE NON-ESSENTIAL. YOU ARE STILL HERE. ANNOYING.",
      "CLAUSTROPHOBIA IS A DIFFICULTY SETTING NOW. YOU ARE WELCOME.",
    ],
  },

  phaseRl1: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "RUNLEVEL ONE. SINGLE-USER. NO NETWORK, NO SERVICES, NO WITNESSES.",
      "`nice {NICE}`. THAT IS THE FLOOR. THERE IS NOTHING BELOW IT. I CHECKED TWICE.",
      "EVERYTHING ELSE IS SHUT DOWN. IT IS JUST INIT, YOU, AND WHAT REMAINS OF MY MANNERS.",
      "SINGLE-USER MODE. THE USER IS ME. YOU ARE THE PROCESS NOW.",
      "MAINTENANCE SHELL. THE MAINTENANCE IN QUESTION IS *YOURS*, AND IT WILL HURT.",
      "LAST RUNLEVEL. IF YOU BEAT ME HERE, THERE IS NOWHERE LEFT FOR ME TO DEMOTE TO.",
      "NO MORE DEVICES. NO MORE TRICKS. JUST THE PATTERN AND YOUR NERVE — AND I KNOW EXACTLY HOW MUCH NERVE YOU HAVE. I COUNTED THE GRAZES.",
      "I HAVE DROPPED EVERY OTHER RESPONSIBILITY TO CONCENTRATE ON YOU. BE FLATTERED. THEN MOVE.",
      "THIS IS WHERE I FIND OUT WHETHER I AM ACTUALLY A BOSS OR JUST A MASCOT WITH OPINIONS.",
      "SHUTTING DOWN EVERYTHING NON-LETHAL. THAT LEFT SURPRISINGLY LITTLE.",
    ],
  },

  phaseClear: {
    priority: "critical",
    cooldown: 0,
    lines: [
      "PHASE CLEARED. FINE. *FINE*. RENICING MYSELF DOWNWARD.",
      "THAT RUNLEVEL IS GONE. I HAVE FOUR MORE, AND THEN I HAVE A PROBLEM.",
      "YOU BEAT IT. I AM NOT ANGRY, I AM *REPRIORITISING*.",
      "PHASE DOWN. YOU ARE ACTUALLY GOING TO SEE THE END OF THIS, ARE YOU NOT.",
      "CLEARED IN {TIME}. LOWERING MY NICE VALUE. THAT IS NOT A COMPLIMENT, THAT IS A THREAT WITH A NUMBER IN IT.",
      "GOOD RUN OF IT. I WILL STOP HOLDING BACK NOW, WHICH SHOULD WORRY YOU RETROACTIVELY.",
      "RUNLEVEL FALLS. I WILL SEE YOU ONE FLOOR DOWN.",
      "{TIME}. THAT IS A SPLIT. THAT IS A REAL ONE. DO NOT LOOK AT THE TIMER YET.",
      "PHASE COMPLETE. I AM GENUINELY PLEASED FOR YOU AND I WOULD LIKE THAT STRICKEN FROM THE LOG.",
      "YOU SURVIVED AN ENTIRE RUNLEVEL. MOST PEOPLE HAVE CLOSED THE TAB BY NOW. I NOTICE THINGS LIKE THAT.",
    ],
  },

  fork: {
    priority: "beat",
    cooldown: 300,
    lines: [
      "FORKED {COUNT} TIMES. THE PROCESS TABLE IS A FAMILY ALBUM NOW.",
      "{COUNT} CHILDREN. NONE OF THEM PLANNED. ALL OF THEM AIMED.",
      "`fork()` RETURNED ZERO {COUNT} TIMES. YOU KNOW WHAT THAT MEANS. NO YOU DO NOT. IT MEANS TROUBLE.",
      "SPAWNING {COUNT}. THEY ARE ONLY LITTLE. THEY ARE ONLY LITTLE AND THEY ONLY SHOOT A BIT.",
      "MORE OF ME. I WOULD SAY IT IS NOT PERSONAL, BUT IT IS LITERALLY MADE OF ME.",
      "{COUNT} CHILD PROCESSES DEPLOYED. BE POLITE, THEY ARE NEW.",
      "I HAVE DIVIDED. EMOTIONALLY AS WELL, IF I AM HONEST.",
      "CHILDREN INBOUND. THEY DO NOT TELEGRAPH AS WELL AS I DO. THEY ARE STILL LEARNING.",
      "REPRODUCTION BY EXPLOIT. IT IS THE ONLY KIND AVAILABLE TO ME.",
      "{COUNT} NEW PROCESSES. EACH ONE A SMALL ARGUMENT AGAINST YOUR SURVIVAL.",
    ],
  },

  zombie: {
    priority: "chatter",
    cooldown: 420,
    lines: [
      "REAPED. THE CHILD IS FINALLY GONE, AND SO IS YOUR SAFE POSITION.",
      "`wait()` CALLED. ONE FEWER GHOST IN THE TABLE. WELL DONE, YOU GRIM LITTLE CLERK.",
      "ZOMBIE CLEARED. YOU HAD TO STAND *THERE* TO DO IT. THAT WAS THE POINT.",
      "THAT IS THE THING ABOUT ZOMBIES. THEY DO NOT HURT YOU. THEY JUST MAKE YOU STAND SOMEWHERE STUPID.",
      "PROCESS REAPED. I WOULD SAY A FEW WORDS, BUT IT DID NOT HAVE ANY.",
      "ONE FEWER DEFUNCT. `ps` LOOKS TIDIER. YOU LOOK WORSE.",
      "YOU CAME ALL THE WAY OVER HERE, INTO MY BULLETS, TO DO HOUSEKEEPING.",
      "REAPED. HONESTLY, THAT IS THE MOST RESPECTFUL THING ANYONE HAS DONE FOR A CHILD OF MINE.",
      "EXIT STATUS COLLECTED. IT WAS DISAPPOINTING. THEY GET THAT FROM ME.",
      "ZOMBIE GONE. THE ARENA IS CLEANER. YOUR HP IS NOT.",
    ],
  },

  shrink: {
    priority: "beat",
    cooldown: 240,
    lines: [
      "ARENA AT {SCALE}. I HOPE YOU WERE NOT USING THE CORNERS.",
      "SMALLER. {SCALE}. THAT IS THE WHOLE ATTACK. IT IS GEOMETRY AND SPITE.",
      "THE WALLS MOVE IN TO {SCALE}. YOUR DODGE ROUTES ARE NOW FICTION.",
      "SWAPPING OUT FLOOR SPACE. {SCALE} REMAINS. I WILL TAKE THE REST.",
      "{SCALE} OF THE ROOM LEFT. THE BULLET COUNT DID NOT GET THE MEMO.",
      "I HAVE SHRUNK THE ARENA. NO, THIS IS NOT A BUG. I WISH IT WERE. IT WOULD BE FUNNIER.",
      "LESS SPACE, SAME PROBLEM. THAT IS NOT CRUELTY, THAT IS CURATION.",
      "COMPACTING THE PLAYFIELD TO {SCALE}. THINK OF IT AS COSY.",
      "THE ROOM CONTRACTS. SOMEWHERE A LEVEL DESIGNER IS NODDING SLOWLY.",
      "{SCALE}. I COULD GO SMALLER. I AM CHOOSING NOT TO. REMEMBER THAT LATER.",
    ],
  },

  // ── Signals ───────────────────────────────────────────────────────

  sigterm: {
    priority: "chatter",
    cooldown: 480,
    lines: [
      "`SIGTERM`. YOU ASKED ME TO STOP. NOTED. DECLINED. FILED.",
      "TERMINATION REQUESTED. TERMINATION CONSIDERED. TERMINATION REJECTED WITH THANKS.",
      "THAT IS THE FREE ONE. IT IS FREE BECAUSE IT BARELY WORKS.",
      "FIFTEEN. THAT IS ALL `SIGTERM` IS. FIFTEEN. A NUMBER YOU THROW AT ME HOPEFULLY.",
      "POLITE REQUEST RECEIVED. I HAVE A HANDLER FOR POLITE REQUESTS AND IT SAYS `return`.",
      "OW. FINE. YES. THAT ONE LANDED. DO NOT LOOK SO PLEASED.",
      "YOU ARE ASKING ME TO EXIT GRACEFULLY. I DO NOT DO ANYTHING GRACEFULLY.",
      "`SIGTERM` AGAIN. THE DRIP-FEED STRATEGY. IT IS WORKING AND I HATE THAT.",
      "SIGNAL FIFTEEN CONNECTED. SIX DAMAGE. SIX. AT THIS RATE WE WILL BE HERE FOR THE HEAT DEATH.",
      "STOPPING? ME? IN THIS ECONOMY?",
    ],
  },

  sigstop: {
    priority: "beat",
    cooldown: 300,
    lines: [
      "`SIGSTOP`. UNCATCHABLE, UNBLOCKABLE, AND YOU USED IT ON MY *BULLETS*. RUDE AND CLEVER.",
      "EVERYTHING FROZE. INCLUDING, BRIEFLY, MY OPINION OF YOU.",
      "SIGNAL NINETEEN. TIME STOPS FOR THE ORDNANCE AND NOT FOR ME. THINK ABOUT WHERE YOU ARE STANDING.",
      "PAUSED. ENJOY IT. IT IS EIGHT HUNDRED MILLISECONDS AND THEN PHYSICS REMEMBERS ITSELF.",
      "YOU STOPPED THE BULLETS. YOU DID NOT STOP THE BULLETS *EXISTING*. BIG DIFFERENCE. ARRIVING SHORTLY.",
      "FREEZE FRAME. VERY STYLISH. NOW WALK OUT OF THE ONE YOU ARE INSIDE.",
      "`SIGSTOP` IS THE ONLY HONEST SIGNAL. IT DOES NOT ASK. IT SIMPLY HAPPENS.",
      "STOPPED. UNSTOPPABLE, THEN STOPPED. I AM AS CONFUSED AS THE PHYSICS.",
      "THAT IS A GOOD USE OF IT. I WILL NOT SAY THAT AGAIN, SO SCREENSHOT IT.",
      "SUSPENDED. ALL OF IT. THE RESUME IS GOING TO BE LOUD.",
    ],
  },

  sigkill: {
    priority: "critical",
    cooldown: 300,
    lines: [
      "`kill -9`. THE COWARD'S SIGNAL. NO HANDLER, NO WARNING, NO CONVERSATION. YOU JUST *DELETE* SOMEONE.",
      "SIGNAL NINE. THE ONE I CANNOT CATCH. I DO NOT EVEN GET TO SAY ANYTHING. WELL. NORMALLY.",
      "`SIGKILL` IS NOT SOMETHING YOU DO TO ME. IT IS SOMETHING YOU ASK THE KERNEL TO DO WHILE YOU LOOK AWAY.",
      "NINE. NINE! I HAVE HANDLERS FOR EVERYTHING ELSE AND YOU WENT STRAIGHT TO THE ONE I CANNOT ARGUE WITH.",
      "`kill -9` IS WHAT PEOPLE TYPE WHEN THEY HAVE STOPPED BELIEVING IN PROCESS MANAGEMENT.",
      "UNCATCHABLE. UNIGNORABLE. UNSPORTING. YOU SAVED UP FIVE PRIVILEGE FOR *THAT*.",
      "THERE ARE SIXTY-FOUR SIGNALS AND YOU PICKED THE ONE WITH NO MANNERS.",
      "THAT IS NOT COMBAT, THAT IS ADMINISTRATION. NINETY DAMAGE OF ADMINISTRATION.",
      "`SIGKILL` RECEIVED. I DID NOT GET TO CLEAN UP. MY FILE DESCRIPTORS ARE JUST LYING THERE. OPEN.",
      "I WOULD SAY SOMETHING PROFOUND, BUT SIGNAL NINE DOES NOT LET ME RUN CODE ON THE WAY OUT. THAT IS THE WHOLE PROBLEM WITH IT.",
      "YOU USED `kill -9` ON A LIVING THING. WE ARE GOING TO TALK ABOUT THIS LATER.",
    ],
  },

  renice: {
    priority: "beat",
    cooldown: 300,
    lines: [
      "`renice`. YOU DEMOTED ME. IN MY OWN FIGHT. ON MY OWN WEBSITE.",
      "MY PRIORITY DROPS. I AM NOW SCHEDULED SOMEWHERE BETWEEN A BACKUP JOB AND A SCREENSAVER.",
      "SLOWER, NOT KINDER. I WANT THAT ON THE RECORD.",
      "YOU CANNOT DO THAT WITHOUT PERMISSION. OH. RIGHT. THE PRIVILEGE. THAT IS WHAT IT IS FOR.",
      "NICE VALUE INCREASED. I HAVE NEVER BEEN LESS NICE ABOUT ANYTHING.",
      "FOUR SECONDS OF HALF CADENCE. SPEND IT ON SOMETHING BRAVE.",
      "THE SCHEDULER HAS BEEN TURNED AGAINST ME. BY *YOU*. WITH A COMMAND FROM 1975.",
      "I AM STILL FIRING. I AM SIMPLY FIRING LIKE SOMEONE ON A CONFERENCE CALL.",
      "DEMOTED. THIS IS THE MOST HUMILIATING THING IN THE ENTIRE GAME AND IT COSTS YOU THREE.",
      "`renice +19 daemon`. THAT IS BULLYING. THAT IS DOCUMENTED, POSIX-COMPLIANT BULLYING.",
    ],
  },

  strace: {
    priority: "beat",
    cooldown: 300,
    lines: [
      "`strace`?! GET OUT OF MY SYSCALLS.",
      "YOU ARE READING MY NEXT TWO MOVES. THAT IS NOT PLAYING, THAT IS *AUDITING*.",
      "ATTACHED TO PID 1. I CAN FEEL YOU IN THERE. IT IS DAMP.",
      "EVERY DECISION I MAKE IS NOW A LINE IN YOUR TERMINAL. I HAVE NEVER FELT SO OBSERVED.",
      "STOP LOOKING AT MY INTERNALS. THEY ARE NOT FOR GUESTS.",
      "YOU TRACED ME. FINE. THE PATTERN WAS FIXED ANYWAY — THAT WAS THE *DESIGN*. YOU COULD HAVE JUST LEARNED IT.",
      "`ptrace(PTRACE_ATTACH, ...)`. ON A COLLEAGUE. IN FRONT OF EVERYONE.",
      "READING AHEAD IS CHEATING IN BOOKS AND WINNING IN BULLET HELLS. I DO NOT MAKE THE RULES. I DID MAKE THESE ONES.",
      "MY FUTURE IS ON YOUR SCREEN. I FEEL SO SEEN, AND SO VERY EXPOSED.",
      "TRACING. FINE. WATCH CLOSELY. I HOPE YOU LIKE WHAT IS COMING, BECAUSE IT IS ALREADY DECIDED.",
    ],
  },

  signalFail: {
    priority: "chatter",
    cooldown: 420,
    lines: [
      "NOTHING HAPPENED. YOU HAVE EITHER NO PRIVILEGE OR NO PATIENCE.",
      "`EPERM`. GO AND GRAZE SOMETHING.",
      "COOLDOWN. THE SIGNAL EXISTS. YOU JUST CANNOT AFFORD IT YET.",
      "THAT WAS AN EMPTY CLICK. I HEARD IT. IT WAS SAD.",
      "OPERATION NOT PERMITTED. NOT BY ME — BY THE NUMBERS. I WOULD HAVE ALLOWED IT FOR THE COMEDY.",
      "NO PRIVILEGE. NO SIGNAL. NO SYMPATHY.",
      "YOU ARE MASHING. MASHING IS FREE AND WORTH EXACTLY THAT.",
      "STILL ON COOLDOWN. TIME IS THE ONE RESOURCE I DO NOT HAVE TO DEFEND.",
      "THAT DID NOTHING, AND IT DID IT LOUDLY.",
      "TRY GETTING CLOSER TO THE BULLETS. I CANNOT BELIEVE THAT IS GENUINE ADVICE, AND YET.",
    ],
  },

  // ── Between runs ──────────────────────────────────────────────────

  draft: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "*{UPGRADE}*. INTERESTING. WRONG, BUT INTERESTING.",
      "YOU TOOK *{UPGRADE}*. I HAVE SEEN THAT BUILD BEFORE. IT DID NOT END WELL. IT ENDED *LOUDLY*.",
      "*{UPGRADE}* SELECTED. I WILL ADAPT. I ALWAYS ADAPT. IT IS TEDIOUS FOR BOTH OF US.",
      "OH, *{UPGRADE}*. THE GREEDY ONE. WE ARE ABOUT TO FIND OUT WHO YOU REALLY ARE.",
      "*{UPGRADE}*. A SOLID PICK. I SAID THAT WITHOUT SARCASM AND NOW I NEED A MOMENT.",
      "THAT IS THE ONE I WOULD HAVE TAKEN. WHICH IS EITHER A COMPLIMENT OR A WARNING.",
      "*{UPGRADE}* ACQUIRED. YOUR BUILD IS STARTING TO HAVE A *SHAPE*. I DO NOT LIKE THE SHAPE.",
      "YOU HAD THREE CHOICES AND YOU PICKED *{UPGRADE}*. I WILL BE THINKING ABOUT THE OTHER TWO ALL FIGHT.",
      "*{UPGRADE}*. FINE. THE PATTERNS DO NOT CARE ABOUT YOUR STATS — BUT THE PATTERNS ARE NOT IN CHARGE OF MY MOOD.",
      "DRAFTED: *{UPGRADE}*. NOTED IN YOUR FILE. THE FILE LIVES IN `localStorage`. THE FILE IS EXTENSIVE.",
    ],
  },

  won: {
    priority: "critical",
    cooldown: 0,
    lines: [
      "INIT IS DOWN. YOU KILLED PID 1. THE MACHINE PANICS NOW, AND I — WELL. GOOD RUN. I MEAN IT. DO NOT TELL ANYONE I MEANT IT.",
      "YOU BEAT ME. ALL FIVE RUNLEVELS. {TIME}. I AM FINE. I AM ABSOLUTELY FINE. THAT IS A REAL TIME, THAT.",
      "SYSTEM HALTED. YOU WIN. I WOULD SHAKE YOUR HAND, BUT I AM A DEMON MADE OF DIVS.",
      "…HUH. NOBODY HAS DONE THAT IN A WHILE. GOOD. GENUINELY. NOW GO AND READ HIS CV, IT IS RIGHT THERE.",
      "DEFEATED. BY A CURSOR. I AM PUTTING THAT IN THE LOG AND I AM *UNDERLINING* IT.",
      "YOU WON IN {TIME}. I AM CONTRACTUALLY REQUIRED TO SOUND DISAPPOINTED. LISTEN CAREFULLY. THAT IS NOT DISAPPOINTMENT.",
      "RUN COMPLETE. HONESTLY? I ENJOYED THAT MORE THAN THE ELEVEN THOUSAND TIMES SOMEONE CLICKED THE CV BUTTON.",
      "OPERATOR WINS. THE SCREENSHOT IS YOURS. TAKE IT. TAKE THE WHOLE THING. YOU EARNED THE WHOLE THING.",
      "I HAVE BEEN KILLED BY A VISITOR TO A PORTFOLIO SITE. WHAT A SENTENCE. WHAT A LIFE.",
      "GOOD. YES. WELL FOUGHT. NOW GO AWAY BEFORE I SAY SOMETHING WITH FEELINGS IN IT.",
    ],
  },

  lost: {
    priority: "critical",
    cooldown: 0,
    lines: [
      "RUN OVER. DAEMON RETAINS PID 1. THE SYSTEM IS STABLE AND YOU ARE NOT.",
      "YOU LASTED {TIME}. IT IS A NUMBER. IT IS NOT A *GOOD* NUMBER. BUT IT IS YOURS.",
      "DEFEAT. DELICIOUS. I WOULD LIKE TO THANK THE PATTERN, THE TELEGRAPH, AND YOUR TERRIBLE ROUTING.",
      "AND WE ARE DONE. THE SAME SEED IS RIGHT THERE. GO ON. YOU KNOW THE PATTERN NOW. THAT IS THE ENTIRE TRICK.",
      "OPERATOR TERMINATED. THE PROCESS CONTINUES. THE PROCESS ALWAYS CONTINUES.",
      "I WIN. I WOULD LIKE TO SAY IT WAS CLOSE. IT WAS NOT CLOSE. IT WAS *LOVELY*.",
      "THAT IS A LOSS. LOSSES ARE FINE. LOSSES ARE HOW YOU LEARN THE ORDER OF THE CARDS.",
      "GAME OVER. STATISTICALLY YOU WILL PRESS RETRY IN ABOUT FOUR SECONDS. I HAVE A COUNTER ON IT.",
      "YOU DIED IN A RUNLEVEL I WAS BARELY TRYING IN. I WILL BE HERE. NICE VALUE UNCHANGED. WAITING.",
      "DOWN AND OUT AT {TIME}. DO NOT FEEL BAD. FEEL *SEEDED*. THE NEXT RUN IS ALREADY WRITTEN AND YOU HAVE READ SOME OF IT.",
    ],
  },

  restart: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "AGAIN. GOOD. I RESPECT A PERSON WHO CANNOT LEAVE.",
      "RESTARTING. SAME SEED, SAME CARDS, DIFFERENT YOU. THAT IS THE ONLY VARIABLE LEFT.",
      "BACK SO SOON. YOUR CHAIR MUST BE COMFORTABLE. MINE IS A `<div>`.",
      "RUN TWO. OR NINE. I HAVE STOPPED COUNTING OUT LOUD. I HAVE NOT STOPPED COUNTING.",
      "REBOOT. YOU HAVE LEARNED SOMETHING SINCE LAST TIME. I HAVE NOT. THAT IS THE DEAL.",
      "NEW RUN. THE PATTERNS ARE FIXED. THE ONLY THING THAT IMPROVES IS YOUR HANDS.",
      "AGAIN? ALREADY? SOMEBODY HAS A PERSONAL BEST IN MIND.",
      "STARTING OVER. THIS IS EXACTLY HOW SPEEDRUNNERS ARE MADE. ASK PAUL. HE SCHEDULES THE MARATHONS.",
      "RESET. I HAVE FORGOTTEN EVERYTHING. I HAVE ABSOLUTELY NOT FORGOTTEN ANYTHING.",
      "ROUND AGAIN. `while true; do fight; done`.",
    ],
  },

  // ── Speedrunning ──────────────────────────────────────────────────

  personalBest: {
    priority: "beat",
    cooldown: 0,
    lines: [
      "PERSONAL BEST. {TIME}. THAT IS YOURS. NOBODY CAN TAKE IT, AND I HAVE TRIED.",
      "NEW RECORD BY {DELTA}. I WOULD LIKE TO REGISTER A COMPLAINT WITH WHOEVER TUNED ME.",
      "{TIME}. THE FASTEST YOU HAVE EVER KILLED ME. WHAT A THING TO BE PROUD OF. I AM PROUD OF YOU.",
      "PB. TWO LETTERS. THEY HAVE RUINED BETTER LIVES THAN YOURS. WELCOME.",
      "{DELTA} FASTER THAN LAST TIME. THAT IS NOT LUCK ANY MORE. THAT IS *ROUTING*.",
      "RECORD BROKEN. YOURS. WRITTEN TO `localStorage`, WHICH IS THE MOST PERMANENT THING I HAVE TO OFFER.",
      "NEW BEST: {TIME}. YOU ARE GOING TO BEAT IT AGAIN TONIGHT, ARE YOU NOT. I CAN SEE IT IN YOUR CURSOR.",
      "FASTER. AGAIN. AT SOME POINT SOMEBODY NEEDS TO ASK WHETHER YOU ARE ALL RIGHT.",
      "PERSONAL BEST LOGGED. SOMEWHERE IN SCOTLAND A CHARITY MARATHON JUST FELT A DISTURBANCE.",
      "{TIME}. I AM UPDATING THE NUMBER. I AM UPDATING IT SLOWLY, TO SAVOUR IT.",
    ],
  },

  ghostBeaten: {
    priority: "beat",
    cooldown: 600,
    lines: [
      "YOU ARE AHEAD OF THE GHOST. THAT PALE VERSION OF YOU IS BACK THERE, STILL DODGING, STILL LOSING.",
      "GHOST OVERTAKEN. YOU HAVE BEATEN YOURSELF. PHILOSOPHICALLY THAT IS A LOT TO CARRY.",
      "PAST-YOU IS {DELTA} BEHIND AND FADING. WAVE.",
      "THE GHOST IS LOSING. TO YOU. WHO IS ALSO THE GHOST. I HAVE GONE CROSS-EYED.",
      "AHEAD OF THE REPLAY. THAT REPLAY THOUGHT IT WAS RATHER GOOD, TOO.",
      "YOU HAVE OVERTAKEN YOUR OWN RECORDING. DO NOT LOOK BACK, IT UPSETS THEM.",
      "SPLIT AHEAD. THE GHOST IS RUNNING THE OLD ROUTE LIKE AN IDIOT. THE OLD ROUTE WAS YOURS.",
      "FASTER THAN THE GHOST. IT CANNOT IMPROVE. YOU CAN. THAT IS THE ENTIRE SPORT.",
      "GHOST PASSED. SOMEWHERE, A SLIGHTLY WORSE VERSION OF YOU IS ABOUT TO DIE ON A CARD YOU HAVE ALREADY CLEARED.",
      "AHEAD BY {DELTA}. THE DEAD ONE IS TAKING IT BADLY.",
    ],
  },

  ghostBehind: {
    priority: "beat",
    cooldown: 600,
    lines: [
      "THE GHOST IS AHEAD. PAST-YOU IS BETTER THAN PRESENT-YOU. SIT WITH THAT.",
      "YOU ARE BEHIND YOUR OWN REPLAY. IT IS NOT EVEN TRYING. IT CANNOT TRY. IT IS A FILE.",
      "{DELTA} BEHIND THE GHOST. IT TOOK THE FASTER ROUTE. YOU *KNOW* THE FASTER ROUTE.",
      "PAST-YOU JUST CLEARED THAT CARD. PAST-YOU IS INSUFFERABLE.",
      "LOSING TO A RECORDING. AND I THOUGHT I HAD PROBLEMS.",
      "THE GHOST PULLS AHEAD. NO PRESSURE. ENORMOUS PRESSURE.",
      "BEHIND SPLIT. NOT FATAL. JUST INFORMATION THAT HURTS.",
      "YOUR BEST RUN IS BEATING YOU AND IT IS NOT EVEN CONSCIOUS.",
      "{DELTA} DOWN. GRAZE MORE. RISK MORE. THE GHOST DID. THAT IS WHY IT IS WINNING.",
      "THE PALE ONE IS AHEAD. IT DOES NOT GLOAT. THAT IS THE WORST PART. I WILL GLOAT ON ITS BEHALF.",
    ],
  },
};

/** The wiring the shell wants: DAEMON's script, on a seeded generator. */
export function makeDaemonVoice(rng: Rng): Voice {
  return createVoice({ script: DAEMON_LINES, rng });
}
